import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Tab, Tabs, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, TablePagination, Avatar, Chip, Button, IconButton, 
  Tooltip, Select, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, 
  DialogTitle, CircularProgress, Switch
} from '@mui/material';
import {  PersonOutlined, MoreVert as MoreVertIcon, Mail as MailIcon, Info as InfoIcon, 
  Delete as DeleteIcon, Edit as EditIcon} from '@mui/icons-material';
import { firebaseHelper } from 'ui';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { TeamsInterface } from 'ts-interfaces';
import { uppercaseFirstLetter } from '../libs/util';
import { AuthContext } from '../auth/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TeamMembersComponent = ({designFirmID,teamMembers, pendingInvites,teamsLoading, currentSeatsUsed, totalSeatsAvailable}) => {
  const {currentUser } = useContext(AuthContext);
  
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<TeamsInterface | null>(null);
  const [editingMember, setEditingMember] = useState<string | null>(null);

  // Filter out archived members
  const activeTeamMembers = teamMembers.filter(member => member.status !== 'archived');
  const activePendingInvites = pendingInvites.filter(invite => invite.status !== 'archived');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  // Handle role change for a team member
  const handleRoleChange = async (member: TeamsInterface, newRole) => {
    try {
      if (!member.id) return;

      // Check if changing from admin to a seat-consuming role would exceed seat limit
      const wasAdmin = member.role === 'admin';
      const willBeAdmin = newRole === 'admin';
      if (wasAdmin && !willBeAdmin && currentSeatsUsed >= totalSeatsAvailable) {
        alert(`Cannot change role: No seats available. Your plan allows ${totalSeatsAvailable} team members.`);
        setEditingMember(null);
        return;
      }

      const memberRef = doc(firebaseHelper.db, 'teams', member.id);
      await updateDoc(memberRef, {
        role: newRole,
        updatedAt: Date.now()
      });

      // No need to update state manually as onSnapshot will handle it
      setEditingMember(null);
    } catch (error) {
      console.error('Error updating member role:', error);
    }
  };

  // Handle deleting a team member
  const openDeleteConfirmDialog = (member: TeamsInterface) => {
    setMemberToDelete(member);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete?.id) return;
    
    try {
      const memberRef = doc(firebaseHelper.db, 'teams', memberToDelete.id);
      // Instead of deleting, update the status to archived
      await updateDoc(memberRef, {
        status: 'archived',
        updatedAt: Date.now()
      });
      
      // Close the dialog
      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      
      // No need to update state manually as onSnapshot will handle it
    } catch (error) {
      console.error('Error archiving team member:', error);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle status change for a team member
  const handleStatusChange = async (member: TeamsInterface) => {
    if (!member.id) return;
    try {
      const newStatus = member.status === 'active' ? 'inactive' : 'active';
      const teamMemberRef = doc(firebaseHelper.db, 'teams', member.id);
      await updateDoc(teamMemberRef, { status: newStatus });
    } catch (error) {
      console.error('Error updating member status:', error);
    }
  };

  // Handle status change for an invited team member
  const handleStatusChangeForPendingInvitation = async (member: TeamsInterface) => {
    if (!member.id) return;
    try {
      const newStatus = member.status === 'invited' ? 'invited_inactive' : 'invited';
      const teamMemberRef = doc(firebaseHelper.db, 'teams', member.id);
      await updateDoc(teamMemberRef, { status: newStatus });
    } catch (error) {
      console.error('Error updating member status:', error);
    }
  };

  // Get current data for pagination
  const currentTeamMembers = activeTeamMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const currentPendingInvites = activePendingInvites.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Empty state
  const EmptyState = ({ message }) => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        py: 4, 
        bgcolor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: 1,
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}
    >
      <InfoIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
      <Typography variant="body1" color="textSecondary">
        {message}
      </Typography>
    </Box>
  );

  // Loading state
  if (teamsLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, width: '95%', borderColor: 'white', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper sx={{p: 3, borderRadius: 2, width: '95%', borderColor: 'white' }}>
      <Box sx={{ borderRadius: 2, width: '100%', bgcolor: 'background.paper' }}>
        <Box sx={{ padding: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="team tabs"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1">Team Members ({activeTeamMembers.length})</Typography>
                </Box>
              } 
              {...a11yProps(0)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1">Pending Invitations ({activePendingInvites.length})</Typography>
                </Box>
              } 
              {...a11yProps(1)} 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {activeTeamMembers.length === 0 ? (
            <EmptyState message="There are no team members yet. Invite people to join your team." />
          ) : (
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="team members table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name/Email</TableCell>
                      <TableCell>Pro</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Join Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTeamMembers.map((member) => (
                      <TableRow
                        key={member.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {member.email ? member.email.charAt(0).toUpperCase() : '?'}
                            </Avatar>
                            {member.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: member.status === 'active' ? 'success.main' : 'text.disabled',
                                mr: 1
                              }}
                            />
                            {uppercaseFirstLetter(member.status)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {editingMember === member.id ? (
                            <Select
                              value={member.role}
                              size="small"
                              onChange={(e) => handleRoleChange(member, e.target.value)}
                              onBlur={() => setEditingMember(null)}
                              autoFocus
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="designer">Designer</MenuItem>
                              <MenuItem value="designer_and_admin">Designer & Admin</MenuItem>
                            </Select>
                          ) : (
                            <Chip 
                              label={uppercaseFirstLetter(member.role)} 
                              size="small" 
                              color={member.role === 'admin' || member.role === 'designer_and_admin' ? 'primary' : 'default'}
                              variant={member.role === 'admin' || member.role === 'designer_and_admin' ? 'filled' : 'outlined'}
                              onClick={() => setEditingMember(member.id)}
                              icon={<EditIcon fontSize="small" />}
                            />
                          )}
                        </TableCell>
                        <TableCell>{new Date(member.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          {currentUser && member.email === currentUser.email ? (
                            // Don't show delete icon for current user
                            <></>
                          ) : (
                            <Tooltip title="Delete Team Member">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => openDeleteConfirmDialog(member)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Status toggle switch */}
                          <Tooltip title={`Set ${member.status === 'active' ? 'Inactive' : 'Active'}`}>
                            <Switch
                              size="small"
                              checked={member.status === 'active'}
                              onChange={() => handleStatusChange(member)}
                              color="success"
                              inputProps={{ 'aria-label': 'toggle active status' }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10]}
                component="div"
                count={teamMembers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {activePendingInvites.length === 0 ? (
            <EmptyState message="No pending invitations." />
          ) : (
            <Paper sx={{ width: '100%', mb: 2 }}>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="pending invitations table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Pro</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Invite Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentPendingInvites.map((invite) => (
                      <TableRow
                        key={invite.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              <MailIcon fontSize="small" />
                            </Avatar>
                            {invite.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: invite.status === 'invited' ? 'success.main' : 'text.disabled',
                                mr: 1
                              }}
                            />
                            {uppercaseFirstLetter( invite.status === 'invited' ? 'Active' : 'Inactive')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {editingMember === invite.id ? (
                            <Select
                              value={invite.role}
                              size="small"
                              onChange={(e) => handleRoleChange(invite, e.target.value)}
                              onBlur={() => setEditingMember(null)}
                              autoFocus
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="designer">Designer</MenuItem>
                              <MenuItem value="designer_and_admin">Designer & Admin</MenuItem>
                            </Select>
                          ) : (
                            <Chip 
                              label={uppercaseFirstLetter(invite.role)} 
                              size="small"
                              variant="outlined"
                              onClick={() => setEditingMember(invite.id)}
                              icon={<EditIcon fontSize="small" />}
                            />
                          )}
                        </TableCell>
                        <TableCell>{new Date(invite.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip 
                            label={uppercaseFirstLetter(invite.status)} 
                            size="small" 
                            color="warning"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {currentUser && invite.email === currentUser.email ? (
                            // Don't show delete icon for current user
                            <></>
                          ) : (
                            <Tooltip title="Delete Invitation">
                              <IconButton 
                                size="small"
                                color="error"
                                onClick={() => openDeleteConfirmDialog(invite)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Status toggle switch */}
                          <Tooltip title={`Set ${invite.status === 'invited' ? 'Inactive' : 'Active'}`}>
                            <Switch
                              size="small"
                              checked={invite.status === 'invited'}
                              onChange={() => handleStatusChangeForPendingInvitation(invite)}
                              color="success"
                              inputProps={{ 'aria-label': 'toggle active status' }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10]}
                component="div"
                count={pendingInvites.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          )}
        </TabPanel>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`${memberToDelete?.status === 'active' ? 'Remove' : 'Delete'} ${memberToDelete?.status === 'active' ? 'Team Member' : 'Invitation'}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {memberToDelete?.status === 'active' 
              ? `Are you sure you want to remove ${memberToDelete?.email} from your team?` 
              : `Are you sure you want to delete the invitation for ${memberToDelete?.email}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteMember} color="error" autoFocus>
            {memberToDelete?.status === 'active' ? 'Remove' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TeamMembersComponent;